/**
 * Verbes métier du domaine catalogue.
 */
import { ActionLevel } from "../../levels.js";
import { defineService } from "../../service.js";
import type { CatalogRepository, SaveCatalogItemCommand } from "./repository.js";
import { computeMarginPercent } from "./types.js";
import type { CatalogItem, ProjectStockUsage } from "./types.js";
import type {
  ImportCatalogInput,
  ListCatalogInput,
  ListProjectStockUsageInput,
  SaveCatalogItemInput,
  WithdrawStockInput,
} from "./schema.js";

type ItemFields = Omit<SaveCatalogItemInput, "itemId" | "confirmationId">;

/** La marge est déduite du couple coût/prix, jamais saisie : deux champs
 *  indépendants finiraient par se contredire. */
function toCommand(input: ItemFields, itemId?: string): SaveCatalogItemCommand {
  return {
    itemId,
    category: input.category,
    subcategory: input.subcategory ?? null,
    name: input.name,
    unit: input.unit,
    unitCost: input.unitCost,
    unitPrice: input.unitPrice ?? null,
    marginPercent: computeMarginPercent(input.unitCost, input.unitPrice),
    tvaRate: input.tvaRate,
    supplierId: input.supplierId ?? null,
    stockQuantity: input.stockQuantity ?? null,
    reorderThreshold: input.reorderThreshold ?? null,
  };
}

export function createCatalogServices(repo: CatalogRepository) {
  const listerCatalogue = defineService<ListCatalogInput, CatalogItem[]>({
    name: "lister_catalogue",
    level: ActionLevel.Read,
    handler: (ctx, input) =>
      repo.list(ctx, { category: input.category, search: input.search }),
  });

  const enregistrerArticle = defineService<SaveCatalogItemInput, CatalogItem>({
    name: "enregistrer_article",
    level: ActionLevel.ReversibleWrite,
    handler: (ctx, input) => repo.save(ctx, toCommand(input, input.itemId)),
  });

  const importerCatalogue = defineService<ImportCatalogInput, { imported: number }>({
    name: "importer_catalogue",
    level: ActionLevel.ReversibleWrite,
    handler: async (ctx, input) => {
      const imported = await repo.saveMany(ctx, input.items.map((i) => toCommand(i)));
      return { imported };
    },
  });

  /**
   * Sortir du stock sur un chantier.
   *
   * Réservé au bureau, comme le reste du catalogue : un mouvement porte un
   * coût unitaire, donc un prix d'achat, et la migration 20260301001100 ferme
   * cette donnée au terrain. Ouvrir ici ce qu'elle ferme là-bas viderait la
   * règle de son sens. La policy le refuserait de toute façon — ce rôle-ci ne
   * fait qu'éviter au patron un message de base de données.
   *
   * N2 et non N3 : rien ne sort de l'entreprise. Le geste se corrige par un
   * mouvement inverse, pas par une suppression — le journal est en ajout
   * seul.
   */
  const sortirDuStock = defineService<WithdrawStockInput, CatalogItem>({
    name: "sortir_du_stock",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: ["patron", "conducteur", "assistant"],
    handler: (ctx, input) =>
      repo.withdrawStock(ctx, {
        itemId: input.itemId,
        projectId: input.projectId,
        quantity: input.quantity,
        note: input.note ?? null,
      }),
  });

  const consommationChantier = defineService<ListProjectStockUsageInput, ProjectStockUsage[]>({
    name: "consommation_chantier",
    level: ActionLevel.Read,
    handler: (ctx, input) => repo.listProjectStockUsage(ctx, input.projectId),
  });

  return {
    listerCatalogue,
    enregistrerArticle,
    importerCatalogue,
    sortirDuStock,
    consommationChantier,
  };
}

export type CatalogServices = ReturnType<typeof createCatalogServices>;
